import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { LoginFlow } from '@app/shared/+state/users/users.models';
import { NotificationType } from '@app/shared/interfaces';
import { Error } from '@app/shared/models/error';
import { DeviceDetectionService } from '@app/shared/services/device-detection/device-detection.service';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { environment } from '@env/environment';

@Component({
  standalone: false,
  selector: 'sbnb-login-form',
  templateUrl: './login-form.component.html',
  styleUrls: ['./login-form.component.scss'],
})
export class LoginFormComponent implements OnInit {
  loginForm: UntypedFormGroup;
  loading: boolean;
  iosApp: boolean;
  androidApp: boolean;
  loginFlow: LoginFlow = LoginFlow.Link;

  environment = environment;

  @Input() redirectUrl: string;
  @Input() email: string;

  @Output() stateChange: EventEmitter<{
    mode: 'login' | 'forgotpw' | 'link' | 'email' | 'code';
    email: string;
    trusted: boolean;
  }> = new EventEmitter();
  generatingMagicLink: boolean;
  isMobile: boolean;

  constructor(
    private fb: UntypedFormBuilder,
    private toast: ToastNotificationsService,
    private authService: AuthenticationService,
    private deviceDetectionService: DeviceDetectionService
  ) {}

  ngOnInit() {
    this.loading = false;
    this.iosApp = this.deviceDetectionService.isIosApp();
    this.androidApp = this.deviceDetectionService.isAndroidApp();

    this.deviceDetectionService.isMobileViewport().subscribe((res) => {
      this.isMobile = res;
      this.loginFlow = this.isMobile || this.androidApp || this.iosApp ? LoginFlow.Code : LoginFlow.Link;
    });

    this.loginForm = this.fb.group({
      email: [this.email, [Validators.required, Validators.email]],
      password: ['', [Validators.required]],
    });
  }

  login(email: string, password: string): void {
    this.loading = true;

    this.authService.login(email, password, this.redirectUrl, this.loginFlow).subscribe((res: Error | any) => {
      this.loading = false;

      if ('flow' in res) {
        this.stateChange.emit({ mode: res.flow, email, trusted: res.device_trusted });
        return;
      }

      if ('errorCode' in res) {
        let message = '';

        if (res.errorCode === 102) {
          message = 'Incorrect email or password. Reset your password, or login using an email link.';
        } else {
          message = res.errorDetail || 'Unable to log you in at this time.';
        }

        this.toast.open(message, 'Dismiss', NotificationType.Error);
      }
    });
  }

  private resetForm(): void {
    Object.keys(this.loginForm.controls).forEach((key) => {
      const control = this.loginForm.get(key);
      control.setErrors(null);
      control.markAsUntouched();
    });
  }

  generateMagicLink() {
    this.resetForm();

    const email = this.loginForm.get('email').value;

    if (!email) {
      // set an error only on the email field
      this.loginForm.get('email').setErrors({ required: true });
      this.loginForm.get('email').markAsTouched();
      return;
    }

    this.generatingMagicLink = true;

    this.authService.requestLoginMagicLink(email, this.loginFlow).subscribe(
      (res: any) => {
        this.generatingMagicLink = false;
        this.stateChange.emit({ mode: res.flow === 'link' ? 'link' : 'code', email, trusted: res.device_trusted });
      },
      (err) => {
        this.generatingMagicLink = false;
        this.toast.open('We ran into an error requesting your magic link.', 'Dismiss', NotificationType.Error);
      }
    );
  }
}
